import './styles/CategoryModal.scss';

import { Grid } from '@mui/material';
import { Category, CreateCategory } from '@thro-types/leihbar';

import { Formik } from 'formik';
import { omit, pick } from 'lodash';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import { useLeihbarCategoriesMutation } from 'api/hooks/leihbar';
import MultistepForm from 'app/components/MultistepForm';
import { actions as AlertActions } from 'app/containers/Dashboard/Alert/slice';
import { translations } from 'locales/i18n';
import { validateForm } from 'utils/FormErrorHelper';

import { CategoryInformation } from './components/CategoryInformation';
import { categoryValidationSchema } from './validation';

interface Props {
  closeHandler: Function;
  setDisabledCancel: Function;
  category: Category | undefined;
}

export function CategoryModal(props: Props) {
  const [submitting, setSubmitting] = useState(false);

  const { t } = useTranslation();
  const dispatch = useDispatch();

  const { create, update } = useLeihbarCategoriesMutation();

  const initialValues = () => {
    return props.category ? { ...pick(props.category, ['id', 'name']) } : { name: '' };
  };

  const handleSaveClick = async (values: CreateCategory & { id?: number }) => {
    if (submitting) {
      return;
    }
    setSubmitting(true);
    if (props.setDisabledCancel) {
      props.setDisabledCancel(true);
    }

    const omitFields = ['id'] as const;
    let saveBody = omit(values, ...omitFields);

    if (values.id) {
      try {
        await update.mutateAsync({
          id: values.id,
          data: saveBody
        });
        dispatch(
          AlertActions.success(t(translations.pages.leihbar.category.alerts.successfulUpdate()))
        );
      } catch (err) {
        dispatch(AlertActions.error(t(translations.pages.leihbar.category.alerts.failedUpdate())));
        setSubmitting(false);
      }
    } else {
      try {
        await create.mutateAsync(saveBody);
        dispatch(
          AlertActions.success(t(translations.pages.leihbar.category.alerts.successfulCreate()))
        );
      } catch (err) {
        dispatch(AlertActions.error(t(translations.pages.leihbar.category.alerts.failedCreate())));
        setSubmitting(false);
      }
    }
    props.closeHandler();
  };

  const steps = ['Information'];

  const SettingsModalStep = ({ step }) => {
    switch (step) {
      case 0:
        return <CategoryInformation />;
      default:
        return null;
    }
  };

  return (
    <Grid item className="categoryModal">
      <Formik
        initialValues={initialValues()}
        onSubmit={values => handleSaveClick(values)}
        validationSchema={categoryValidationSchema(t)}
        validate={values => {
          return validateForm(values, categoryValidationSchema(t), dispatch);
        }}
      >
        <MultistepForm
          submitting={submitting}
          steps={steps}
          onCancel={props.closeHandler}
          hideStepper
        >
          {activeStep => (
            <Grid container className="categoryModal__body">
              <SettingsModalStep step={activeStep} />
            </Grid>
          )}
        </MultistepForm>
      </Formik>
    </Grid>
  );
}
